import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Building2, User, Phone, Mail, MapPin, CreditCard, Database, FileText, RefreshCw, X, AlertCircle, HardDrive, Image, Folder, FileBox, Receipt, Edit, Shield, Gift, Key, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubscriptionModal = ({ isOpen, onClose, tenant, plans, onAssign }) => {
  const [selectedPlan, setSelectedPlan] = useState('');
  const [duration, setDuration] = useState(1);
  const [loading, setLoading] = useState(false);

  const calculatePrice = () => {
    if (!selectedPlan) return { base: 0, discount: 0, final: 0 };
    
    const plan = plans.find(p => p.id === selectedPlan);
    if (!plan) return { base: 0, discount: 0, final: 0 };

    const basePrice = plan.price * duration;
    let discountPercent = 0;
    
    if (duration === 3) discountPercent = 5;
    else if (duration === 6) discountPercent = 10;
    else if (duration >= 12) discountPercent = 15;

    const discountAmount = basePrice * (discountPercent / 100);
    const finalPrice = basePrice - discountAmount;

    return {
      base: basePrice,
      discount: discountPercent,
      discountAmount,
      final: finalPrice
    };
  };

  const handleSubmit = async () => {
    if (!selectedPlan) {
      alert('Please select a plan');
      return;
    }
    setLoading(true);
    await onAssign(selectedPlan, duration);
    setLoading(false);
  };

  if (!isOpen) return null;

  const pricing = calculatePrice();

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-xl shadow-2xl w-full max-w-md"
        >
          <div className="p-6 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-violet-50 rounded-lg">
                <RefreshCw className="text-violet-600" size={24} />
              </div>
              <h3 className="text-xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Assign / Renew Subscription
              </h3>
            </div>
            <button
              onClick={onClose}
              data-testid="close-subscription-modal-btn"
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>{tenant?.business_name}</strong>
              </p>
              <p className="text-xs text-blue-600 mt-1">Store ID: {tenant?.store_id}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Select Plan
              </label>
              <select
                data-testid="subscription-plan-select"
                value={selectedPlan}
                onChange={(e) => setSelectedPlan(e.target.value)}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
              >
                <option value="">Choose a plan...</option>
                {plans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name} - ₹{plan.price}/month
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Duration
              </label>
              <select
                data-testid="subscription-duration-select"
                value={duration}
                onChange={(e) => setDuration(parseInt(e.target.value))}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
              >
                <option value={1}>1 Month</option>
                <option value={3}>3 Months (5% off)</option>
                <option value={6}>6 Months (10% off)</option>
                <option value={12}>12 Months (15% off)</option>
              </select>
            </div>

            {selectedPlan && (
              <div className="p-4 bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Base Price:</span>
                  <span className="font-medium">₹{pricing.base.toFixed(2)}</span>
                </div>
                {pricing.discount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-emerald-600">Discount ({pricing.discount}%):</span>
                    <span className="font-medium text-emerald-600">-₹{pricing.discountAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className="pt-2 border-t border-emerald-300 flex justify-between">
                  <span className="font-semibold text-slate-900">Total:</span>
                  <span className="font-bold text-lg text-violet-600">₹{pricing.final.toFixed(2)}</span>
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                data-testid="confirm-subscription-btn"
                onClick={handleSubmit}
                disabled={loading || !selectedPlan}
                className="flex-1 bg-gradient-to-r from-violet-600 to-purple-600 text-white px-4 py-2 rounded-lg hover:shadow-lg transition-all disabled:opacity-50"
              >
                {loading ? 'Assigning...' : 'Assign Subscription'}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

// Edit Tenant Details Modal
const EditTenantModal = ({ isOpen, onClose, tenant, onSave }) => {
  const [formData, setFormData] = useState({
    business_name: '',
    contact_person: '',
    mobile_number: '',
    email: '',
    address: '',
    postal_code: '',
    city: '',
    district: '',
    state: '',
    gst_number: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (tenant) {
      setFormData({
        business_name: tenant.business_name || '',
        contact_person: tenant.contact_person || '',
        mobile_number: tenant.mobile_number || '',
        email: tenant.email || '',
        address: tenant.address || '',
        postal_code: tenant.postal_code || '',
        city: tenant.city || '',
        district: tenant.district || '',
        state: tenant.state || '',
        gst_number: tenant.gst_number || ''
      });
    }
  }, [tenant]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.business_name || !formData.contact_person || !formData.mobile_number) {
      setError('Business name, contact person and mobile number are required');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await onSave(formData);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update tenant');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
      >
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Edit className="text-blue-600" size={24} />
            </div>
            <h3 className="text-xl font-semibold">Edit Tenant Details</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X size={20} />
          </button>
        </div>
        <div className="p-6 space-y-4 overflow-y-auto max-h-[60vh]">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Business Name *</label>
              <input
                type="text"
                name="business_name"
                value={formData.business_name}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Contact Person *</label>
              <input
                type="text"
                name="contact_person"
                value={formData.contact_person}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Mobile Number *</label>
              <input
                type="tel"
                name="mobile_number"
                value={formData.mobile_number}
                onChange={handleChange}
                maxLength={10}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">GST Number</label>
              <input
                type="text"
                name="gst_number"
                value={formData.gst_number}
                onChange={handleChange}
                placeholder="22AAAAA0000A1Z5"
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Postal Code</label>
              <input
                type="text"
                name="postal_code"
                value={formData.postal_code}
                onChange={handleChange}
                maxLength={6}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Address</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">City</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">District</label>
              <input
                type="text"
                name="district"
                value={formData.district}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">State</label>
              <input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
        <div className="p-6 border-t border-slate-200 flex gap-3">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// Manual Verify Modal
const ManualVerifyModal = ({ isOpen, onClose, tenant, onVerify }) => {
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!notes.trim()) {
      alert('Please provide notes explaining why manual verification is needed');
      return;
    }
    setLoading(true);
    await onVerify(notes);
    setLoading(false);
    setNotes('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl shadow-2xl w-full max-w-md"
      >
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-50 rounded-lg">
              <Shield className="text-emerald-600" size={24} />
            </div>
            <h3 className="text-xl font-semibold">Manual Verification</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X size={20} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800">
              <strong>{tenant?.business_name}</strong> hasn't completed OTP verification. 
              Manual verification will allow them to login without OTP.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Verification Notes <span className="text-red-500">*</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              placeholder="Enter reason for manual verification (e.g., OTP delivery failed, verified via call, etc.)"
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
            />
          </div>
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
            <FileText size={16} className="inline mr-2" />
            After verification, you can generate a PIN for the user to login.
          </div>
          <div className="flex gap-3 pt-4">
            <button onClick={onClose} className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50">
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || !notes.trim()}
              className="flex-1 bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
            >
              {loading ? 'Verifying...' : 'Verify & Approve'}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Grace Extension Modal
const GraceExtensionModal = ({ isOpen, onClose, tenant, onConfirm }) => {
  const [days, setDays] = useState(7);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    await onConfirm(days, notes);
    setLoading(false);
    onClose();
    setNotes('');
    setDays(7);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl shadow-2xl w-full max-w-md"
      >
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-50 rounded-lg">
              <Gift className="text-emerald-600" size={24} />
            </div>
            <h3 className="text-xl font-semibold">Extend Grace Period</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X size={20} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Days to Extend</label>
            <input
              type="number"
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value) || 0)}
              min={1}
              max={90}
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Notes (Optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Reason for grace extension..."
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
            />
          </div>
          <div className="flex gap-3 pt-4">
            <button onClick={onClose} className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50">
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || days < 1}
              className="flex-1 bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
            >
              {loading ? 'Extending...' : `Extend by ${days} Days`}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// PIN Management Modal
const PinModal = ({ isOpen, onClose, tenant, onGenerate, onRemove }) => {
  const [loading, setLoading] = useState(false);

  const handleGenerate = async () => {
    setLoading(true);
    await onGenerate();
    setLoading(false);
  };

  const handleRemove = async () => {
    setLoading(true);
    await onRemove();
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl shadow-2xl w-full max-w-md"
      >
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-violet-50 rounded-lg">
              <Key className="text-violet-600" size={24} />
            </div>
            <h3 className="text-xl font-semibold">Admin PIN Management</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X size={20} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>{tenant?.business_name}</strong>
            </p>
            <p className="text-xs text-blue-600 mt-1">Store ID: {tenant?.store_id}</p>
          </div>

          {tenant?.admin_pin ? (
            <>
              <div className="p-4 bg-violet-50 border border-violet-200 rounded-lg text-center">
                <p className="text-sm text-violet-600 mb-2">Current Admin PIN</p>
                <p className="text-3xl font-mono font-bold text-violet-800 tracking-widest">
                  {tenant.admin_pin}
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleGenerate}
                  disabled={loading}
                  className="flex-1 bg-violet-600 text-white px-4 py-2 rounded-lg hover:bg-violet-700 disabled:opacity-50"
                >
                  {loading ? 'Generating...' : 'Regenerate PIN'}
                </button>
                <button
                  onClick={handleRemove}
                  disabled={loading}
                  className="flex-1 bg-red-50 text-red-600 border border-red-200 px-4 py-2 rounded-lg hover:bg-red-100 disabled:opacity-50"
                >
                  Remove PIN
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg text-center text-slate-500">
                No admin PIN has been generated for this tenant.
              </div>
              <button
                onClick={handleGenerate}
                disabled={loading}
                className="w-full bg-violet-600 text-white px-4 py-2 rounded-lg hover:bg-violet-700 disabled:opacity-50"
              >
                {loading ? 'Generating...' : 'Generate Admin PIN'}
              </button>
            </>
          )}

          <p className="text-xs text-slate-500 text-center">
            This PIN allows the admin/tenant to bypass OTP verification during login.
          </p>
          <button onClick={onClose} className="w-full px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50">
            Close
          </button>
        </div>
      </motion.div>
    </div>
  );
};

const TenantDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showGraceModal, setShowGraceModal] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [storageDetails, setStorageDetails] = useState(null);
  const [loadingStorage, setLoadingStorage] = useState(false);

  useEffect(() => {
    fetchTenantDetail();
    fetchPlans();
  }, [id]);

  const fetchTenantDetail = async () => {
    try {
      const response = await axios.get(`${API}/admin/tenants/${id}`);
      setTenant(response.data);
      // Auto-fetch storage details
      fetchStorageDetails();
    } catch (error) {
      console.error('Error fetching tenant detail:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStorageDetails = async () => {
    setLoadingStorage(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/tenants/${id}/storage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStorageDetails(response.data);
    } catch (error) {
      console.error('Error fetching storage details:', error);
    } finally {
      setLoadingStorage(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/admin/plans`);
      setPlans(response.data);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const handleAssignSubscription = async (planId, durationMonths) => {
    try {
      await axios.put(`${API}/admin/tenants/${id}/subscription`, {
        plan_id: planId,
        duration_months: durationMonths
      });
      setShowSubscriptionModal(false);
      fetchTenantDetail();
      alert('Subscription assigned successfully!');
    } catch (error) {
      console.error('Error assigning subscription:', error);
      alert('Failed to assign subscription. Please try again.');
    }
  };

  const handleUpdateDetails = async (formData) => {
    await axios.put(`${API}/admin/tenants/${id}/details`, formData);
    fetchTenantDetail();
  };

  const handleManualVerify = async (notes) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/admin/tenants/${id}/manual-verify`, { notes }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTenantDetail();
    } catch (error) {
      console.error('Error verifying tenant:', error);
      alert(error.response?.data?.detail || 'Failed to verify tenant');
    }
  };

  const handleGraceExtension = async (days, notes) => {
    try {
      await axios.post(`${API}/admin/tenants/${id}/extend-grace`, { days, notes });
      setShowGraceModal(false);
      fetchTenantDetail();
    } catch (error) {
      console.error('Error extending grace:', error);
      alert('Failed to extend grace period');
    }
  };

  const handleGeneratePin = async () => {
    try {
      await axios.post(`${API}/admin/tenants/${id}/generate-pin`);
      fetchTenantDetail();
    } catch (error) {
      console.error('Error generating PIN:', error);
      alert('Failed to generate PIN');
    }
  };

  const handleRemovePin = async () => {
    try {
      await axios.delete(`${API}/admin/tenants/${id}/admin-pin`);
      fetchTenantDetail();
      setShowPinModal(false);
    } catch (error) {
      console.error('Error removing PIN:', error);
      alert('Failed to remove PIN');
    }
  };

  const handleToggleStatus = async () => {
    try {
      await axios.put(`${API}/admin/tenants/${id}/status`, {
        is_active: !tenant.tenant.is_active,
        deactivation_reason: tenant.tenant.is_active ? 'Deactivated by admin' : null
      });
      fetchTenantDetail();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-violet-200 border-t-violet-600"></div>
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Tenant not found</p>
      </div>
    );
  }

  return (
    <div data-testid="tenant-detail" className="space-y-6">
      <button
        data-testid="back-btn"
        onClick={() => navigate('/tenants')}
        className="flex items-center gap-2 text-slate-600 hover:text-violet-600 transition-colors"
      >
        <ArrowLeft size={20} />
        Back to Tenants
      </button>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {tenant.tenant.business_name}
          </h1>
          <p className="text-slate-600">Store ID: <span className="font-mono font-semibold text-violet-600">{tenant.tenant.store_id}</span></p>
        </div>
        {tenant.tenant.is_active ? (
          <span className="badge badge-success text-base px-4 py-2">Active</span>
        ) : (
          <span className="badge badge-error text-base px-4 py-2">Inactive</span>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Business Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-2 bg-white rounded-xl border border-slate-100 p-6 space-y-6"
        >
          <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Business Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-violet-50 rounded-lg">
                <Building2 size={20} className="text-violet-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Business Name</p>
                <p className="font-medium text-slate-900">{tenant.tenant.business_name}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="p-2 bg-violet-50 rounded-lg">
                <User size={20} className="text-violet-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Contact Person</p>
                <p className="font-medium text-slate-900">{tenant.tenant.contact_person}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="p-2 bg-violet-50 rounded-lg">
                <Phone size={20} className="text-violet-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Mobile Number</p>
                <p className="font-medium text-slate-900">{tenant.tenant.mobile_number}</p>
              </div>
            </div>

            {tenant.tenant.email && (
              <div className="flex items-start gap-3">
                <div className="p-2 bg-violet-50 rounded-lg">
                  <Mail size={20} className="text-violet-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Email</p>
                  <p className="font-medium text-slate-900">{tenant.tenant.email}</p>
                </div>
              </div>
            )}

            <div className="flex items-start gap-3 md:col-span-2">
              <div className="p-2 bg-violet-50 rounded-lg">
                <MapPin size={20} className="text-violet-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Address</p>
                <p className="font-medium text-slate-900">{tenant.tenant.address}</p>
                <p className="text-sm text-slate-600">
                  {tenant.tenant.city && `${tenant.tenant.city}, `}
                  {tenant.tenant.state} - {tenant.tenant.postal_code}
                </p>
              </div>
            </div>

            {tenant.tenant.gst_number && (
              <div className="flex items-start gap-3">
                <div className="p-2 bg-violet-50 rounded-lg">
                  <FileText size={20} className="text-violet-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">GST Number</p>
                  <p className="font-mono font-medium text-slate-900">{tenant.tenant.gst_number}</p>
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* Subscription & Usage */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="space-y-6"
        >
          {/* Subscription */}
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <div className="flex items-center gap-2 mb-4">
              <CreditCard className="text-orange-500" size={20} />
              <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Subscription
              </h3>
            </div>
            {tenant.plan ? (
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-slate-500">Plan</p>
                  <p className="font-semibold text-lg text-violet-600">{tenant.plan.name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Price</p>
                  <p className="font-medium">₹{tenant.plan.price.toLocaleString('en-IN')} / {tenant.plan.duration}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Valid Until</p>
                  <p className="font-medium">
                    {tenant.tenant.subscription_valid_until
                      ? new Date(tenant.tenant.subscription_valid_until).toLocaleDateString('en-IN')
                      : 'N/A'}
                  </p>
                </div>
                <button
                  data-testid="renew-subscription-btn"
                  onClick={() => setShowSubscriptionModal(true)}
                  className="w-full mt-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg hover:shadow-lg transition-all"
                >
                  Renew / Change Plan
                </button>
              </div>
            ) : (
              <div>
                <p className="text-slate-500 mb-4">No active subscription</p>
                <button
                  data-testid="assign-subscription-btn"
                  onClick={() => setShowSubscriptionModal(true)}
                  className="w-full px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-lg hover:shadow-lg transition-all"
                >
                  Assign Subscription
                </button>
              </div>
            )}
            
            {tenant.tenant.deactivation_reason && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle size={16} className="text-red-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-900">Deactivation Reason:</p>
                    <p className="text-sm text-red-700 mt-1">{tenant.tenant.deactivation_reason}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Usage Stats */}
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Database className="text-emerald-500" size={20} />
              <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Usage
              </h3>
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-slate-500 mb-1">Bills Generated</p>
                <div className="flex items-end gap-2">
                  <p className="text-2xl font-bold text-violet-600">{tenant.usage?.bills_count || 0}</p>
                  {tenant.plan?.bills_limit && (
                    <p className="text-sm text-slate-500 mb-1">/ {tenant.plan.bills_limit}</p>
                  )}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm text-slate-500">Storage Used</p>
                  <button
                    onClick={() => document.getElementById('storage-breakdown')?.scrollIntoView({ behavior: 'smooth' })}
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                  >
                    <HardDrive size={12} />
                    View Breakdown ↓
                  </button>
                </div>
                <div className="flex items-end gap-2">
                  <p className="text-2xl font-bold text-emerald-600">
                    {(tenant.usage?.storage_used_mb || 0) < 1 
                      ? `${((tenant.usage?.storage_used_mb || 0) * 1024).toFixed(0)} KB`
                      : `${(tenant.usage?.storage_used_mb || 0).toFixed(2)} MB`}
                  </p>
                  {tenant.subscription?.storage_limit_mb && (
                    <p className="text-sm text-slate-500 mb-1">
                      / {tenant.subscription.storage_limit_mb >= 1024 
                          ? `${(tenant.subscription.storage_limit_mb / 1024).toFixed(1)} GB`
                          : `${tenant.subscription.storage_limit_mb} MB`}
                    </p>
                  )}
                </div>
                {tenant.subscription?.storage_limit_mb && (
                  <div className="mt-2 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${
                        (tenant.usage?.storage_used_mb / tenant.subscription.storage_limit_mb) >= 0.9
                          ? 'bg-red-500'
                          : (tenant.usage?.storage_used_mb / tenant.subscription.storage_limit_mb) >= 0.75
                            ? 'bg-amber-500'
                            : 'bg-gradient-to-r from-emerald-500 to-green-500'
                      }`}
                      style={{
                        width: `${Math.min((tenant.usage?.storage_used_mb / tenant.subscription.storage_limit_mb) * 100, 100)}%`,
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Storage Breakdown Section */}
      <motion.div
        id="storage-breakdown"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-xl border border-slate-100 p-6 mt-6"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <HardDrive className="text-blue-500" size={20} />
            <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Storage Breakdown
            </h3>
          </div>
          <button
            onClick={fetchStorageDetails}
            disabled={loadingStorage}
            className="flex items-center gap-2 px-3 py-1.5 text-sm bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw size={14} className={loadingStorage ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>

        {loadingStorage && !storageDetails ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-200 border-t-blue-600"></div>
          </div>
        ) : storageDetails ? (
          <div className="space-y-6">
            {/* Summary Bar */}
            <div className={`p-4 rounded-lg ${
              storageDetails.storage_summary?.critical ? 'bg-red-50 border border-red-200' :
              storageDetails.storage_summary?.warning ? 'bg-amber-50 border border-amber-200' :
              'bg-blue-50 border border-blue-200'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Total Storage</span>
                <span className="text-lg font-bold">
                  {storageDetails.storage_summary?.used_mb < 1 
                    ? `${(storageDetails.storage_summary?.used_mb * 1024).toFixed(0)} KB`
                    : `${storageDetails.storage_summary?.used_mb?.toFixed(2)} MB`}
                  {' / '}
                  {storageDetails.storage_summary?.limit_mb >= 1024 
                    ? `${(storageDetails.storage_summary?.limit_mb / 1024).toFixed(1)} GB`
                    : `${storageDetails.storage_summary?.limit_mb} MB`}
                </span>
              </div>
              <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all ${
                    storageDetails.storage_summary?.critical ? 'bg-red-500' :
                    storageDetails.storage_summary?.warning ? 'bg-amber-500' : 'bg-blue-500'
                  }`}
                  style={{ width: `${Math.min(storageDetails.storage_summary?.percentage || 0, 100)}%` }}
                />
              </div>
              <p className="text-xs text-slate-500 mt-1 text-right">
                {storageDetails.storage_summary?.percentage?.toFixed(1)}% used
              </p>
            </div>

            {/* Images & Attachments */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Image className="text-violet-500" size={18} />
                <h4 className="font-medium text-slate-700">Images & Attachments</h4>
                <span className="ml-auto text-sm font-semibold text-violet-600">
                  {(storageDetails.images_and_attachments?.total_mb * 1024).toFixed(1)} KB
                </span>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-violet-50 rounded-lg border border-violet-100">
                  <p className="text-xs text-violet-600 mb-1">Product Images</p>
                  <p className="text-lg font-bold text-violet-700">
                    {storageDetails.images_and_attachments?.breakdown?.product_images?.count || 0}
                  </p>
                  <p className="text-xs text-slate-500">
                    {((storageDetails.images_and_attachments?.breakdown?.product_images?.size_mb || 0) * 1024).toFixed(1)} KB
                  </p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <p className="text-xs text-blue-600 mb-1">Business Logo</p>
                  <p className="text-lg font-bold text-blue-700">
                    {storageDetails.images_and_attachments?.breakdown?.business_logo?.count || 0}
                  </p>
                  <p className="text-xs text-slate-500">
                    {((storageDetails.images_and_attachments?.breakdown?.business_logo?.size_mb || 0) * 1024).toFixed(1)} KB
                  </p>
                </div>
                <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                  <p className="text-xs text-emerald-600 mb-1">Payment Attachments</p>
                  <p className="text-lg font-bold text-emerald-700">
                    {storageDetails.images_and_attachments?.breakdown?.payment_attachments?.count || 0}
                  </p>
                  <p className="text-xs text-slate-500">
                    {((storageDetails.images_and_attachments?.breakdown?.payment_attachments?.size_mb || 0) * 1024).toFixed(1)} KB
                  </p>
                </div>
              </div>
            </div>

            {/* Master Data */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Folder className="text-amber-500" size={18} />
                <h4 className="font-medium text-slate-700">Master Data</h4>
                <span className="ml-auto text-sm font-semibold text-amber-600">
                  {((storageDetails.master_data?.total || 0) * 1024).toFixed(1)} KB
                </span>
              </div>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                {storageDetails.master_data?.counts && Object.entries(storageDetails.master_data.counts).map(([key, count]) => (
                  <div key={key} className="p-2 bg-amber-50 rounded-lg border border-amber-100 text-center">
                    <p className="text-xs text-amber-600 capitalize">{key}</p>
                    <p className="text-lg font-bold text-amber-700">{count}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Transaction Data */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Receipt className="text-rose-500" size={18} />
                <h4 className="font-medium text-slate-700">Transaction Data</h4>
                <span className="ml-auto text-sm font-semibold text-rose-600">
                  {((storageDetails.transaction_data?.total || 0) * 1024).toFixed(1)} KB
                </span>
              </div>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                {storageDetails.transaction_data?.counts && Object.entries(storageDetails.transaction_data.counts).map(([key, count]) => (
                  <div key={key} className="p-2 bg-rose-50 rounded-lg border border-rose-100 text-center">
                    <p className="text-xs text-rose-600 capitalize">{key.replace(/_/g, ' ')}</p>
                    <p className="text-lg font-bold text-rose-700">{count}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-slate-400">
            <p>Click "Refresh" to load storage breakdown</p>
          </div>
        )}
      </motion.div>

      {/* Admin Controls */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm"
      >
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Admin Controls</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
          {/* Edit Details Button */}
          <button
            onClick={() => setShowEditModal(true)}
            className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200 transition-colors"
          >
            <Edit size={18} />
            Edit Details
          </button>
          
          {/* Manual Verify Button - Only show if not verified */}
          {!tenant?.tenant?.is_verified && (
            <button
              onClick={() => setShowVerifyModal(true)}
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium bg-amber-50 text-amber-600 hover:bg-amber-100 border border-amber-200 transition-colors"
            >
              <Shield size={18} />
              Manual Verify
            </button>
          )}
          
          <button
            onClick={handleToggleStatus}
            className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors ${
              tenant?.tenant?.is_active 
                ? 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200' 
                : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100 border border-emerald-200'
            }`}
          >
            {tenant?.tenant?.is_active ? <XCircle size={18} /> : <CheckCircle size={18} />}
            {tenant?.tenant?.is_active ? 'Deactivate' : 'Activate'}
          </button>
          
          <button
            onClick={() => setShowGraceModal(true)}
            className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium bg-emerald-50 text-emerald-600 hover:bg-emerald-100 border border-emerald-200 transition-colors"
          >
            <Gift size={18} />
            Extend Grace
          </button>
          
          <button
            onClick={() => setShowPinModal(true)}
            className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium bg-violet-50 text-violet-600 hover:bg-violet-100 border border-violet-200 transition-colors"
          >
            <Key size={18} />
            {tenant?.tenant?.admin_pin ? 'Manage PIN' : 'Generate PIN'}
          </button>
          
          <button
            onClick={fetchTenantDetail}
            className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium bg-slate-50 text-slate-600 hover:bg-slate-100 border border-slate-200 transition-colors"
          >
            <RefreshCw size={18} />
            Refresh
          </button>
        </div>
        
        {/* Verification Status */}
        {tenant?.tenant?.is_verified ? (
          <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-3">
            <CheckCircle className="text-emerald-600" size={20} />
            <div>
              <p className="text-sm font-medium text-emerald-700">Mobile Verified</p>
              {tenant?.tenant?.verification_type === 'admin_manual' && (
                <p className="text-xs text-emerald-600">Manually verified by admin: {tenant?.tenant?.verification_notes}</p>
              )}
            </div>
          </div>
        ) : (
          <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3">
            <AlertTriangle className="text-amber-600" size={20} />
            <div>
              <p className="text-sm font-medium text-amber-700">Mobile Not Verified</p>
              <p className="text-xs text-amber-600">User cannot login until verified via OTP or manual approval</p>
            </div>
          </div>
        )}
        
        {tenant?.tenant?.admin_pin && (
          <div className="mt-4 p-4 bg-violet-50 border border-violet-200 rounded-lg">
            <p className="text-sm text-violet-600 mb-1">Admin Override PIN (for OTP bypass)</p>
            <p className="text-2xl font-mono font-bold text-violet-800 tracking-widest">{tenant.tenant.admin_pin}</p>
          </div>
        )}
        
        {tenant?.tenant?.deactivation_reason && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600 mb-1">Deactivation Reason</p>
            <p className="text-red-800">{tenant.tenant.deactivation_reason}</p>
          </div>
        )}
      </motion.div>

      {/* Modals */}
      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        tenant={tenant?.tenant}
        plans={plans}
        onAssign={handleAssignSubscription}
      />
      <EditTenantModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        tenant={tenant?.tenant}
        onSave={handleUpdateDetails}
      />
      <ManualVerifyModal
        isOpen={showVerifyModal}
        onClose={() => setShowVerifyModal(false)}
        tenant={tenant?.tenant}
        onVerify={handleManualVerify}
      />
      <GraceExtensionModal
        isOpen={showGraceModal}
        onClose={() => setShowGraceModal(false)}
        tenant={tenant?.tenant}
        onConfirm={handleGraceExtension}
      />
      <PinModal
        isOpen={showPinModal}
        onClose={() => setShowPinModal(false)}
        tenant={tenant?.tenant}
        onGenerate={handleGeneratePin}
        onRemove={handleRemovePin}
      />
    </div>
  );
};

export default TenantDetail;